"use client";

import { useCallback, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { buildOAuthCallbackUrl } from "@/lib/auth-site";
import { cn } from "@/lib/utils";
import { authOAuthButton } from "@/lib/auth-ui";
import { useResetOnBfcache } from "@/lib/use-reset-on-bfcache";
import { setPendingAuth, type PendingAuthMode } from "@/lib/pending-auth";

function FacebookMark({ className }: { className?: string }) {
  return (
    <svg
      className={cn("text-[#1877F2]", className)}
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden
    >
      <path
        fill="currentColor"
        d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.313 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"
      />
    </svg>
  );
}

type FacebookSignInButtonProps = {
  disabled?: boolean;
  /** Shown on the button; pick sign-up vs sign-in copy for the screen. */
  label: string;
  /** Merged onto the button (e.g. Figma-specific sizing). */
  buttonClassName?: string;
  /** Icon color; use `text-white` on solid blue buttons. */
  iconClassName?: string;
  /** Soft blur strip along the bottom (Figma “Pattern” layer). */
  bottomBrandGlow?: boolean;
  /** Which modal to reopen if the user lands back on the site without
   * completing OAuth (back nav, provider error, etc.). Defaults to "login". */
  mode?: PendingAuthMode;
  /** Where to land the user after successful OAuth. Forwarded to the
   * /auth/callback handler so the homepage prompt-resume flow can run. */
  next?: string;
};

export function FacebookSignInButton({
  disabled = false,
  label,
  buttonClassName,
  iconClassName,
  bottomBrandGlow = false,
  mode = "login",
  next,
}: FacebookSignInButtonProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useResetOnBfcache(
    useCallback(() => {
      setBusy(false);
      setError(null);
    }, []),
  );

  async function handleClick() {
    setError(null);
    setBusy(true);

    const redirectTo = buildOAuthCallbackUrl(next);
    if (!redirectTo) {
      setError("Could not determine site URL. Set NEXT_PUBLIC_SITE_URL.");
      setBusy(false);
      return;
    }

    const supabase = createClient();
    // skipBrowserRedirect: true is critical — without it, supabase-js
    // navigates to data.url itself *during* this call, which means the
    // setPendingAuth() line below never runs and the modal can't reopen
    // when the user comes back via Back.
    const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "facebook",
      options: { redirectTo, skipBrowserRedirect: true },
    });

    if (oauthError) {
      setError(oauthError.message);
      setBusy(false);
      return;
    }

    if (data.url) {
      setPendingAuth(mode);
      try {
        const back = new URL(window.location.href);
        back.searchParams.set(mode, "1");
        window.history.pushState({}, "", back.toString());
      } catch {
        // ignore — fallback is sessionStorage
      }
      console.log("[auth] redirecting to Facebook OAuth", { mode });
      window.location.assign(data.url);
      return;
    }

    setError("Could not start Facebook sign-in. Try again.");
    setBusy(false);
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        className={cn(authOAuthButton, "rounded-full", buttonClassName)}
        onClick={() => void handleClick()}
        disabled={disabled || busy}
      >
        {bottomBrandGlow ? (
          <span
            className="pointer-events-none absolute bottom-[-9px] left-0 right-0 z-0 h-4 bg-[#0866FF] blur-[2.5px]"
            aria-hidden
          />
        ) : null}
        <span className="relative z-[1] flex items-center justify-center gap-2">
          <FacebookMark className={iconClassName} />
          {busy ? "Redirecting…" : label}
        </span>
      </button>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
    </div>
  );
}
